/**
 * DrawingVisual - Pure visual component for rendering drawing blocks.
 *
 * This component ONLY renders the visual representation of a drawing (route/path).
 * NO interaction logic, NO drag handlers, NO selection logic.
 * Behavior wrappers (Selectable, Draggable) handle interaction.
 *
 * Implementation Note:
 * DrawingBlock is a container that requires resolved children (ControlPointBlock, PathSegmentBlock).
 * The resolved drawing data should be stored in block.properties.__resolved_drawing (internal field)
 * using the drawingBlockToDrawing adapter. This is populated by the data loader before rendering.
 *
 * Architecture:
 * Uses pluggable renderer pattern:
 * - StaticDrawingRenderer: Static rendering with modifier support
 * - AnimatedDrawingRenderer: Two-layer animation rendering (dimmed background + traveled foreground)
 */

import React from 'react'
import type { BlockVisualProps } from '@/components/blocks/Types'
import type { DrawingBlock } from '@/types/blocks/canvas/Drawing'
import type { Drawing } from '@/types/Drawing.types'
import { useBlockRenderContext } from '@/components/blocks/BlockRenderContext'
import { useBlockModifiers } from '@/components/blocks/modifiers/UseBlockModifiers'
import { StaticDrawingRenderer } from './renderers/StaticDrawingRenderer'
import { AnimatedDrawingRenderer } from './renderers/AnimatedDrawingRenderer'

export function DrawingVisual({ block }: BlockVisualProps) {
	const { mode, animationTime } = useBlockRenderContext()

	// Type guard and type assertion
	const isDrawing = block.type === 'drawing'
	const drawingBlock = isDrawing ? (block as DrawingBlock) : null

	// Get resolved drawing data (undefined if not a drawing block)
	const drawing = drawingBlock
		? ((drawingBlock.properties as any).__resolved_drawing as Drawing | undefined)
		: undefined

	// Get modifiers (pre-snap markers, etc.)
	// Always call hook unconditionally - it accepts any PlayBlock_Union
	const modifiers = useBlockModifiers(block)

	// Early returns AFTER all hooks
	if (!isDrawing) {
		console.error('DrawingVisual called with non-drawing block:', block.type)
		return null
	}

	if (!drawing) {
		console.warn('DrawingVisual: No resolved drawing data. Use drawingBlockToDrawing adapter.')
		return (
			<g style={{ pointerEvents: 'none' }}>
				<text
					x="0"
					y="0"
					fill="var(--muted-foreground)"
					fontSize="12"
					fontFamily="monospace"
				>
					Drawing data not resolved
				</text>
			</g>
		)
	}

	// Choose renderer based on mode
	if (mode === 'animated' && animationTime !== undefined) {
		return (
			<AnimatedDrawingRenderer
				drawing={drawing}
				modifiers={modifiers}
				animationTime={animationTime}
			/>
		)
	}

	return <StaticDrawingRenderer drawing={drawing} modifiers={modifiers} />
}
